/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import fastMemoize from 'fast-memoize';
import _ from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Alert, CircularProgress } from '@mui/material';

import { AlignCenter } from '../../components/AlignCenter/AlignCenter';
import { HUMaterialTable, HUMaterialTableProps } from '../../components/HUMaterialTable/HUMaterialTable';
import { HUSpin } from '../../components/HUSpin/HUSpin';
import { SimpleConfirmModal } from '../../components/SimpleConfirmModal/SimpleConfirmModal';
import { SimpleNotifyModal } from '../../components/SimpleNotifyModal/SimpleNotifyModal';
import { Namespaces } from '../../i18n';
import { SuperuserDatasetCategoryApiService } from '../../services/SuperuserDatasetCategoryApiService';
import { SUDatasetCategory } from '../../types/superuser/dataset-category/SUDatasetCategory';
import { SUDatasetCategoryI18n } from '../../types/superuser/dataset-category/SUDatasetCategoryI18n';
import { fetchAdminDatasetsCategories } from '../../utils/queryFns';
import { reactToUpdateAdminDatasetCategories } from '../../utils/reactions';
import { searchSubstring } from '../../utils/searchSubstring';
import { useFormatDate } from '../../utils/useFormatDate';
import { deleteAdminCategoryInitialValues } from './AdminDatasetsCategoriesPage.consts';
import { useAdminDatasetsCategoriesPageStyles } from './AdminDatasetsCategoriesPage.styles';
import { DeleteAdminCategoryDataType } from './AdminDatasetsCategoriesPage.types';
import { DatasetsCategoriesDetailPanel } from './components/DatasetsCategoriesDetailPanel/DatasetsCategoriesDetailPanel';
import { EditDatasetCategoryForm } from './components/EditDatasetCategoryForm/EditDatasetCategoryForm';

export function AdminDatasetsCategoriesPage() {
  const classes = useAdminDatasetsCategoriesPageStyles();
  const { t, i18n } = useTranslation(Namespaces.Admin);

  const { formatDate } = useFormatDate();

  const [isEditFormOpen, setIsEditFormOpen] = useState<boolean>(false);
  const toggleEditForm = useCallback(() => {
    setIsEditFormOpen(s => !s);
  }, []);

  const [category, setCategory] = useState<SUDatasetCategory | null>(null);

  const [deleteCategoryData, setDeleteCategoryData] = useState<DeleteAdminCategoryDataType>(
    deleteAdminCategoryInitialValues,
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isOpenErrorModal, setIsOpenErrorModal] = useState<boolean>(false);

  const i18nNameSelector = useMemo(
    () =>
      fastMemoize(
        (language: string, i18n: SUDatasetCategoryI18n[]) => i18n.find(i => i.language === language)?.title || '—',
      ),
    [],
  );

  const handleAdd = useCallback(() => {
    setCategory(null);
    toggleEditForm();
  }, [toggleEditForm]);

  const handleEdit = useCallback(
    (datasetCategory: SUDatasetCategory) => {
      setCategory(datasetCategory);
      toggleEditForm();
    },
    [toggleEditForm],
  );

  const handleDelete = useCallback(
    (datasetCategory: SUDatasetCategory) => {
      setDeleteCategoryData({
        id: datasetCategory.id,
        name: i18nNameSelector(i18n.language, datasetCategory.i18n),
        isModalOpened: true,
      });
    },
    [i18n.language, i18nNameSelector],
  );

  const queryDatasetsCategories = useQuery(fetchAdminDatasetsCategories.key, fetchAdminDatasetsCategories.fn, {
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
  const datasetsCategories = useMemo(() => queryDatasetsCategories.data?.payload ?? [], [queryDatasetsCategories]);
  const sortedDatasetsCategories = useMemo(
    () => _.sortBy(datasetsCategories, ['updatedAt']).reverse(),
    [datasetsCategories],
  );

  const isDatasetsCategoriesLoading = useMemo(
    () => queryDatasetsCategories.status === 'loading',
    [queryDatasetsCategories.status],
  );

  const isError = useMemo(
    () => queryDatasetsCategories.error && queryDatasetsCategories.status === 'error',
    [queryDatasetsCategories.error, queryDatasetsCategories.status],
  );

  const onConfirmDeleteCategory = async () => {
    try {
      setIsLoading(true);
      setDeleteCategoryData({
        ...deleteAdminCategoryInitialValues,
        isModalOpened: false,
      });
      await SuperuserDatasetCategoryApiService.deleteDatasetCategory({
        datasetCategoryId: deleteCategoryData.id,
      });
      await reactToUpdateAdminDatasetCategories();
      setIsLoading(false);
      setDeleteCategoryData(deleteAdminCategoryInitialValues);
    } catch (e) {
      console.error(e);
      setIsLoading(false);
      setIsOpenErrorModal(true);
    }
  };

  const tableConfig = useMemo<HUMaterialTableProps<SUDatasetCategory>>(
    () => ({
      columns: [
        {
          title: t('datasets.categories.table.labels.thumbnail'),
          render: rowData => <img src={rowData.thumbnail} className={classes.thumbnail} alt="thumbnail" />,
          cellStyle: {
            textAlign: 'center',
          },
          headerStyle: {
            textAlign: 'center',
          },
        },
        {
          title: t('datasets.categories.table.labels.name', {
            currentLanguage: i18n.language.toUpperCase(),
          }),
          render: rowData => i18nNameSelector(i18n.language, rowData.i18n),
          cellStyle: {
            fontWeight: 500,
          },
          customFilterAndSearch: (substring, rowData) =>
            searchSubstring(substring, i18nNameSelector(i18n.language, rowData.i18n)),
        },
        {
          title: t('datasets.categories.table.labels.created_at'),
          render: rowData => formatDate(rowData.createdAt),
        },
        {
          title: t('datasets.categories.table.labels.updated_at'),
          render: rowData => formatDate(rowData.updatedAt),
        },
      ],
      data: sortedDatasetsCategories,
      title: t('datasets.categories.title'),
      actions: [
        rowData => {
          return {
            icon: () => <EditIcon />,
            tooltip: t('datasets.categories.table.labels.edit'),
            onClick: () => handleEdit(rowData),
          };
        },
        rowData => {
          const isDisabled: boolean = rowData.datasets > 0;
          return {
            icon: () => <DeleteIcon />,
            tooltip: isDisabled
              ? t('datasets.categories.table.labels.delete_disabled', {
                  count: rowData.datasets,
                })
              : t('datasets.categories.table.labels.delete'),
            onClick: () => handleDelete(rowData),
            disabled: isDisabled,
          };
        },
        {
          icon: () => <AddIcon />,
          tooltip: t('datasets.categories.table.labels.add_category'),
          isFreeAction: true,
          onClick: handleAdd,
        },
      ],
      options: {
        actionsColumnIndex: -1,
        draggable: false,
        sorting: false,
        search: true,
      },
      detailPanel: [
        {
          tooltip: t('datasets.categories.table.show_more'),
          render: ({ rowData }) => <DatasetsCategoriesDetailPanel rowData={rowData} prepareDate={formatDate} />,
        },
      ],
    }),
    [
      t,
      i18n.language,
      sortedDatasetsCategories,
      handleAdd,
      classes.thumbnail,
      i18nNameSelector,
      formatDate,
      handleEdit,
      handleDelete,
    ],
  );

  return (
    <>
      {isDatasetsCategoriesLoading ? (
        <AlignCenter className={classes.loaderContainer}>
          <CircularProgress color="primary" size={40} />
        </AlignCenter>
      ) : isError ? (
        <Alert severity="error">{t(`${Namespaces.Common}:errors.unexpected_error`)}</Alert>
      ) : (
        <HUSpin spinning={isLoading} delay={50}>
          <HUMaterialTable {...tableConfig} />
          <EditDatasetCategoryForm isOpen={isEditFormOpen} closeOverlay={toggleEditForm} datasetCategory={category} />
        </HUSpin>
      )}
      <SimpleConfirmModal
        open={deleteCategoryData.isModalOpened}
        message={t('datasets.categories.confirmations.delete_category', {
          name: deleteCategoryData.name,
        })}
        onCancel={() => setDeleteCategoryData(deleteAdminCategoryInitialValues)}
        onConfirm={onConfirmDeleteCategory}
      />
      <SimpleNotifyModal
        open={isOpenErrorModal}
        message={t(`${Namespaces.Common}:errors.unexpected_error`)}
        onOk={() => setIsOpenErrorModal(false)}
      />
    </>
  );
}
